import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./BDAIChat.css";
import BDAI_CONFIG from "./config";
import { createApi } from "./api";
import { load, save, uid, downloadBlob } from "./utils";
import ConversationSidebar from "./components/ConversationSidebar";
import DateRangePicker from "./components/DateRangePicker";
import Message from "./components/Message";
import Composer from "./components/Composer";
import Thinking from "./components/Thinking";
import Welcome from "./components/Welcome";
import TableModal from "./components/TableModal";
import {
  ArrowDownIcon, CloseIcon, CollapseIcon, ExpandIcon, HistoryIcon, MinusIcon, PlusIcon, SidebarIcon, SparkIcon,
} from "./components/Icons";

/* Map a message from the API to the shape the UI uses */
function fromServer(m) {
  return {
    id: m.id,
    serverId: m.role === "assistant" ? m.id : null,
    role: m.role,
    content: m.content || "",
    tables: m.tables || [],
    toolsUsed: m.tools_used || [],
    usage: m.usage || null,
    durationMs: m.duration_ms || null,
    feedback: m.feedback ?? null,
    error: m.error || null,
    createdAt: m.created_at,
    range: m.from_date || m.to_date ? { from: m.from_date || "", to: m.to_date || "", label: "Custom" } : null,
  };
}

/**
 * Floating BD AI assistant.
 *
 * <BDAIChat />                                   uses config.js
 * <BDAIChat empCode={user.emp_code} userName={user.first_name}
 *           apiBaseUrl="http://localhost:8000" getAuthHeaders={() => ({Authorization: `Bearer ${token}`})}
 *           hidden={location.pathname === "/login"} />
 */
export default function BDAIChat({
  apiBaseUrl,
  apiPrefix,
  getAuthHeaders,
  empCode,
  userName,
  assistantName,
  hidden = false,
  offset,
}) {
  const cfg = BDAI_CONFIG;
  const name = assistantName || cfg.ASSISTANT_NAME;
  const resolvedEmp = empCode !== undefined ? empCode : cfg.getEmpCode();

  const api = useMemo(
    () =>
      createApi({
        baseUrl: apiBaseUrl !== undefined ? apiBaseUrl : cfg.API_BASE_URL,
        prefix: apiPrefix || cfg.API_PREFIX,
        getAuthHeaders: getAuthHeaders || cfg.getAuthHeaders,
        timeoutMs: cfg.REQUEST_TIMEOUT_MS,
      }),
    [apiBaseUrl, apiPrefix, getAuthHeaders, cfg]
  );

  // ---------------------------------------------------------------- state
  const [open, setOpen] = useState(() => load("open", false));
  const [fullscreen, setFullscreen] = useState(() => load("full", false));
  const [sidebarOpen, setSidebarOpen] = useState(() => load("sidebar", true)); // fullscreen sidebar
  const [historyOverlay, setHistoryOverlay] = useState(false); // compact-mode history drawer
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState("");
  const [activeId, setActiveId] = useState(() => load("conv", null));
  const [messages, setMessages] = useState([]);
  const [loadingConv, setLoadingConv] = useState(false);
  const [sending, setSending] = useState(false);
  const [startedAt, setStartedAt] = useState(0);
  const [draft, setDraft] = useState(() => load("draft", ""));
  const [range, setRange] = useState(() => load("range", null));
  const [suggestions, setSuggestions] = useState(cfg.FALLBACK_SUGGESTIONS);
  const [unread, setUnread] = useState(0);
  const [tableModal, setTableModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [hintVisible, setHintVisible] = useState(() => !load("hintSeen", false));

  const abortRef = useRef(null);
  const viewTokenRef = useRef(0); // changes whenever the visible conversation changes
  const listRef = useRef(null);
  const composerRef = useRef(null);
  const openRef = useRef(open);
  const loadedOnce = useRef(false);
  openRef.current = open;

  // ---------------------------------------------------------------- persistence
  useEffect(() => save("open", open), [open]);
  useEffect(() => save("full", fullscreen), [fullscreen]);
  useEffect(() => save("sidebar", sidebarOpen), [sidebarOpen]);
  useEffect(() => save("conv", activeId), [activeId]);
  useEffect(() => save("range", range), [range]);
  useEffect(() => {
    const t = setTimeout(() => save("draft", draft || null), 300);
    return () => clearTimeout(t);
  }, [draft]);

  const showToast = useCallback((text, kind = "info") => {
    setToast({ text, kind, id: Date.now() });
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // ---------------------------------------------------------------- data loading
  const refreshConversations = useCallback(async () => {
    setConvLoading(true);
    setConvError("");
    try {
      const res = await api.listConversations(resolvedEmp);
      setConversations(res.data || []);
    } catch (e) {
      setConvError(e.message);
    } finally {
      setConvLoading(false);
    }
  }, [api, resolvedEmp]);

  const openConversation = useCallback(
    async (id) => {
      if (abortRef.current) abortRef.current.abort();
      viewTokenRef.current += 1;
      setSending(false);
      setActiveId(id);
      setHistoryOverlay(false);
      setMessages([]);
      if (!id) return;
      const token = viewTokenRef.current;
      setLoadingConv(true);
      try {
        const res = await api.getConversation(id);
        if (token !== viewTokenRef.current) return;
        setMessages((res.messages || []).map(fromServer));
      } catch (e) {
        if (token !== viewTokenRef.current) return;
        if (e.status === 404) setActiveId(null);
        else showToast(e.message, "error");
      } finally {
        if (token === viewTokenRef.current) setLoadingConv(false);
      }
    },
    [api, showToast]
  );

  // first time the panel opens: suggestions, history, last conversation
  useEffect(() => {
    if (!open || loadedOnce.current || hidden) return;
    loadedOnce.current = true;
    api
      .suggestions()
      .then((r) => r.data && r.data.length && setSuggestions(r.data))
      .catch(() => { });
    refreshConversations();
    if (activeId) openConversation(activeId);
  }, [open, hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------- ui effects
  useEffect(() => {
    if (open) {
      setUnread(0);
      const t = setTimeout(() => composerRef.current && composerRef.current.focus(), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, fullscreen]);

  useEffect(() => {
    if (!(open && fullscreen)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, fullscreen]);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 280;
    const last = messages[messages.length - 1];
    if (nearBottom || (last && last.role === "user") || sending) scrollToBottom(true);
  }, [messages, sending, scrollToBottom]);

  useEffect(() => {
    // jump to bottom when a conversation finishes loading / panel opens
    if (!loadingConv) setTimeout(() => scrollToBottom(false), 30);
  }, [loadingConv, open, fullscreen, scrollToBottom]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 240);
  };

  // keyboard shortcuts: Ctrl/Cmd+K toggle, Esc exit fullscreen / minimise
  useEffect(() => {
    if (hidden) return undefined;
    const onKey = (e) => {
      if (cfg.ENABLE_SHORTCUT && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        setHintVisible(false);
        save("hintSeen", true);
        return;
      }
      if (e.key === "Escape" && openRef.current) {
        if (historyOverlay) setHistoryOverlay(false);
        else if (fullscreen) setFullscreen(false);
        else setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hidden, fullscreen, historyOverlay, cfg.ENABLE_SHORTCUT]);

  // ---------------------------------------------------------------- actions
  const send = useCallback(
    async (text, rangeOverride) => {
      const question = (text ?? draft).trim();
      if (!question || sending) return;
      const useRange = rangeOverride !== undefined ? rangeOverride : range;
      const userMsg = { id: uid(), role: "user", content: question, range: useRange, createdAt: new Date().toISOString() };
      setMessages((m) => [...m, userMsg]);
      if (text === undefined) setDraft("");
      setSending(true);
      setStartedAt(Date.now());

      const controller = new AbortController();
      abortRef.current = controller;
      const token = viewTokenRef.current;
      const convAtSend = activeId;

      try {
        const res = await api.ask(
          {
            question,
            from_date: useRange?.from || null,
            to_date: useRange?.to || null,
            conversation_id: convAtSend,
            emp_code: resolvedEmp || null,
          },
          controller.signal
        );
        if (token !== viewTokenRef.current) {
          refreshConversations(); // user moved to another chat; answer is saved in history
          return;
        }
        setActiveId(res.conversation_id);
        setMessages((m) => [
          ...m,
          {
            id: res.message_id || uid(),
            serverId: res.message_id,
            role: "assistant",
            content: res.answer,
            tables: res.tables || [],
            toolsUsed: res.tools_used || [],
            usage: res.usage,
            durationMs: res.duration_ms,
            notes: res.notes || [],
            feedback: null,
            createdAt: res.created_at || new Date().toISOString(),
          },
        ]);
        setConversations((list) => {
          const now = new Date().toISOString();
          const exists = list.find((c) => c.id === res.conversation_id);
          if (exists) return [{ ...exists, updated_at: now }, ...list.filter((c) => c.id !== res.conversation_id)];
          return [{ id: res.conversation_id, title: res.conversation_title || question.slice(0, 250), pinned: false, created_at: now, updated_at: now }, ...list];
        });
        if (!openRef.current) setUnread((u) => u + 1);
      } catch (e) {
        if (token !== viewTokenRef.current) return;
        if (controller.signal.aborted && e.message === "Request cancelled") {
          setMessages((m) => [...m, { id: uid(), role: "assistant", stopped: true, retry: { question, range: useRange }, createdAt: new Date().toISOString() }]);
        } else {
          setMessages((m) => [...m, { id: uid(), role: "assistant", error: e.message, retry: { question, range: useRange }, createdAt: new Date().toISOString() }]);
        }
      } finally {
        if (token === viewTokenRef.current) setSending(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [draft, sending, range, activeId, api, resolvedEmp, refreshConversations]
  );

  const stop = () => abortRef.current && abortRef.current.abort();

  const newChat = () => {
    if (abortRef.current) abortRef.current.abort();
    viewTokenRef.current += 1;
    setSending(false);
    setActiveId(null);
    setMessages([]);
    setHistoryOverlay(false);
    setTimeout(() => composerRef.current && composerRef.current.focus(), 50);
  };

  const retry = (msg) => {
    if (!msg.retry) return;
    setMessages((m) => {
      const idx = m.findIndex((x) => x.id === msg.id);
      return idx > 0 ? m.slice(0, idx - 1) : m.filter((x) => x.id !== msg.id);
    });
    setTimeout(() => send(msg.retry.question, msg.retry.range), 0);
  };

  const regenerate = (msg) => {
    const idx = messages.findIndex((x) => x.id === msg.id);
    const prevUser = [...messages.slice(0, idx)].reverse().find((x) => x.role === "user");
    if (prevUser) send(prevUser.content, prevUser.range || null);
  };

  const editMessage = (msg) => {
    setDraft(msg.content);
    if (msg.range) setRange(msg.range);
    composerRef.current && composerRef.current.focus();
  };

  const feedback = async (msg, value) => {
    setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, feedback: value || null } : x)));
    try {
      await api.feedback(msg.serverId, value);
      if (value) showToast(value === 1 ? "Thanks for the feedback!" : "Thanks — we'll use this to improve answers.");
    } catch (e) {
      setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, feedback: msg.feedback } : x)));
      showToast(e.message, "error");
    }
  };

  const exportMsg = async (msg, type) => {
    try {
      const { blob, filename } = await api.exportMessage(msg.serverId, type);
      downloadBlob(blob, filename);
    } catch (e) {
      showToast(`Export failed: ${e.message}`, "error");
    }
  };

  const renameConv = async (id, title) => {
    setConversations((l) => l.map((c) => (c.id === id ? { ...c, title } : c)));
    try {
      await api.updateConversation(id, { title });
    } catch (e) {
      showToast(e.message, "error");
      refreshConversations();
    }
  };

  const pinConv = async (id, pinned) => {
    setConversations((l) => {
      const next = l.map((c) => (c.id === id ? { ...c, pinned } : c));
      return [...next.filter((c) => c.pinned), ...next.filter((c) => !c.pinned)];
    });
    try {
      await api.updateConversation(id, { pinned });
    } catch (e) {
      showToast(e.message, "error");
      refreshConversations();
    }
  };

  const deleteConv = async (id) => {
    const before = conversations;
    setConversations((l) => l.filter((c) => c.id !== id));
    if (id === activeId) newChat();
    try {
      await api.deleteConversation(id);
      showToast("Chat deleted");
    } catch (e) {
      setConversations(before);
      showToast(e.message, "error");
    }
  };

  const openPanel = () => {
    setOpen(true);
    setHintVisible(false);
    save("hintSeen", true);
  };

  // ---------------------------------------------------------------- render
  if (hidden) return null;

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const showFollowUps =
    !sending && lastAssistant && !lastAssistant.error && !lastAssistant.stopped && messages[messages.length - 1] === lastAssistant;
  const activeConv = conversations.find((c) => c.id === activeId);
  const style = offset
    ? { "--bdai-offset-bottom": `${offset.bottom ?? 24}px`, "--bdai-offset-right": `${offset.right ?? 24}px` }
    : undefined;

  return (
    <div className="bdai-root" style={style}>
      {/* ---------------- launcher ---------------- */}
      {!open && (
        <div className="bdai-launcher-wrap">
          {hintVisible && (
            <div className="bdai-hint" role="status">
              <button
                type="button"
                className="bdai-hint-close"
                aria-label="Dismiss"
                onClick={() => {
                  setHintVisible(false);
                  save("hintSeen", true);
                }}
              >
                <CloseIcon size={12} />
              </button>
              <strong>Hi{userName ? ` ${userName}` : ""}! 👋</strong>
              <span>Ask me anything about your tenders, team progress or EMD.</span>
            </div>
          )}
          <button
            type="button"
            className="bdai-launcher"
            onClick={openPanel}
            aria-label={`Open ${name}`}
            title={`${name}${cfg.ENABLE_SHORTCUT ? " (Ctrl+K)" : ""}`}
          >
            <span className="bdai-launcher-ring" aria-hidden="true" />
            <SparkIcon size={26} strokeWidth={1.8} />
            {unread > 0 && <span className="bdai-unread">{unread > 9 ? "9+" : unread}</span>}
            {sending && <span className="bdai-launcher-busy" aria-hidden="true" />}
          </button>
        </div>
      )}

      {/* ---------------- panel ---------------- */}
      {open && (
        <section
          className={`bdai-panel ${fullscreen ? "bdai-full" : "bdai-compact"}`}
          role="dialog"
          aria-label={name}
          aria-modal={fullscreen ? "true" : "false"}
        >
          {fullscreen && sidebarOpen && (
            <ConversationSidebar
              conversations={conversations}
              loading={convLoading}
              error={convError}
              activeId={activeId}
              onSelect={openConversation}
              onNew={newChat}
              onRename={renameConv}
              onDelete={deleteConv}
              onPin={pinConv}
              onRefresh={refreshConversations}
            />
          )}

          <div className="bdai-main">
            <header className="bdai-header">
              <div className="bdai-header-left">
                {fullscreen ? (
                  <button
                    type="button"
                    className="bdai-icon-btn bdai-icon-btn-light"
                    onClick={() => setSidebarOpen((s) => !s)}
                    title={sidebarOpen ? "Hide history" : "Show history"}
                    aria-label="Toggle history"
                  >
                    <SidebarIcon size={17} />
                  </button>
                ) : null}
                <div className="bdai-header-avatar"><SparkIcon size={18} /></div>
                <div className="bdai-header-text">
                  <div className="bdai-header-title">{name}</div>
                  <div className="bdai-header-sub">
                    <span className="bdai-status-dot" />
                    {fullscreen && activeConv ? activeConv.title : "Live answers from your BD data"}
                  </div>
                </div>
              </div>
              <div className="bdai-header-actions">
                {!fullscreen && (
                  <button
                    type="button"
                    className="bdai-icon-btn bdai-icon-btn-light"
                    onClick={() => {
                      setHistoryOverlay((h) => !h);
                      if (!conversations.length) refreshConversations();
                    }}
                    title="Chat history"
                    aria-label="Chat history"
                  >
                    <HistoryIcon size={17} />
                  </button>
                )}
                <button type="button" className="bdai-icon-btn bdai-icon-btn-light" onClick={newChat} title="New chat" aria-label="New chat">
                  <PlusIcon size={17} />
                </button>
                <button
                  type="button"
                  className="bdai-icon-btn bdai-icon-btn-light bdai-hide-mobile"
                  onClick={() => {
                    setFullscreen((f) => !f);
                    setHistoryOverlay(false);
                  }}
                  title={fullscreen ? "Exit full screen (Esc)" : "Full screen"}
                  aria-label={fullscreen ? "Exit full screen" : "Full screen"}
                >
                  {fullscreen ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
                </button>
                <button
                  type="button"
                  className="bdai-icon-btn bdai-icon-btn-light"
                  onClick={() => setOpen(false)}
                  title="Minimise (Esc)"
                  aria-label="Minimise"
                >
                  {fullscreen ? <CloseIcon size={17} /> : <MinusIcon size={17} />}
                </button>
              </div>
            </header>

            <div className="bdai-toolbar">
              <DateRangePicker value={range} onChange={setRange} disabled={sending} />
              {resolvedEmp && (
                <span className="bdai-asking-as" title="Questions like “my tenders” use this employee code">
                  Asking as <strong>{resolvedEmp}</strong>
                </span>
              )}
            </div>

            <div className="bdai-body">
              {!fullscreen && historyOverlay && (
                <ConversationSidebar
                  overlay
                  conversations={conversations}
                  loading={convLoading}
                  error={convError}
                  activeId={activeId}
                  onSelect={openConversation}
                  onNew={newChat}
                  onRename={renameConv}
                  onDelete={deleteConv}
                  onPin={pinConv}
                  onRefresh={refreshConversations}
                  onClose={() => setHistoryOverlay(false)}
                />
              )}

              <div className="bdai-messages" ref={listRef} onScroll={onScroll} aria-live="polite">
                <div className="bdai-messages-inner">
                  {loadingConv && (
                    <div className="bdai-loading-conv">
                      <div className="bdai-skeleton bdai-skeleton-user" />
                      <div className="bdai-skeleton bdai-skeleton-bot" />
                      <div className="bdai-skeleton bdai-skeleton-user" />
                    </div>
                  )}

                  {!loadingConv && !messages.length && !sending && (
                    <Welcome name={name} userName={userName} suggestions={suggestions} onPick={(s) => send(s)} compact={!fullscreen} />
                  )}

                  {messages.map((m) => (
                    <Message
                      key={m.id}
                      msg={m}
                      isLast={m === lastAssistant}
                      assistantName={name}
                      onEdit={!sending ? editMessage : null}
                      onRegenerate={!sending ? regenerate : null}
                      onRetry={!sending ? retry : null}
                      onFeedback={feedback}
                      onExport={exportMsg}
                      onExpandTable={setTableModal}
                    />
                  ))}

                  {sending && <Thinking startedAt={startedAt} />}

                  {showFollowUps && (
                    <div className="bdai-followups" aria-label="Suggested follow-ups">
                      {cfg.FOLLOW_UPS.slice(0, fullscreen ? 6 : 4).map((f) => (
                        <button type="button" key={f} className="bdai-followup" onClick={() => send(f)}>
                          {f}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {showScrollBtn && (
                <button type="button" className="bdai-scroll-btn" onClick={() => scrollToBottom(true)} aria-label="Scroll to latest">
                  <ArrowDownIcon size={16} />
                </button>
              )}
            </div>

            <Composer
              ref={composerRef}
              value={draft}
              onChange={setDraft}
              onSend={() => send()}
              onStop={stop}
              loading={sending}
              maxLength={cfg.MAX_QUESTION_LENGTH}
              placeholder={fullscreen ? "Ask about tenders, stage progress, team leads, EMD / BG, competitors…" : "Ask about tenders, team, EMD…"}
            />
          </div>

          {toast && (
            <div key={toast.id} className={`bdai-toast bdai-toast-${toast.kind}`} role="status">
              {toast.text}
            </div>
          )}
        </section>
      )}

      {tableModal && <TableModal table={tableModal} onClose={() => setTableModal(null)} />}
    </div>
  );
}
